import React, { useState, useEffect, useRef } from 'react';
import { ListTodo, CheckCircle2, Circle, ChevronUp, GripVertical, Pause, Play, Bot, User } from 'lucide-react';
import { Task, TaskPosition } from '../types';

interface TodoOverlayProps {
  tasks: Task[];
  onToggleTask: (id: string) => void;
  onAssignTask?: (id: string, assignee: 'Codey') => void;
  onClear: () => void;
  position: TaskPosition;
  onPositionChange: (pos: TaskPosition) => void;
  isPaused?: boolean;
  onTogglePause?: () => void;
  isAutonomous?: boolean;
  onToggleAutonomous?: () => void;
}

const TodoOverlay: React.FC<TodoOverlayProps> = ({ tasks, onToggleTask, onAssignTask, onClear, position, onPositionChange, isPaused = false, onTogglePause, isAutonomous = false, onToggleAutonomous }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const dragStartOffset = useRef({ x: 0, y: 0 });
  const overlayRef = useRef<HTMLDivElement>(null);

  const allCompleted = tasks.length > 0 && tasks.every(t => t.completed);
  const completedCount = tasks.filter(t => t.completed).length;
  const totalCount = tasks.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  // Unified Drag Handlers
  const startDrag = (clientX: number, clientY: number, target: EventTarget) => {
      // Fix: Don't drag if clicking buttons
      if ((target as HTMLElement).closest('button')) return;
      setIsDragging(true);
      dragStartOffset.current = {
        x: clientX - position.x,
        y: clientY - position.y
      };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    startDrag(e.clientX, e.clientY, e.target);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    const touch = e.touches[0];
    startDrag(touch.clientX, touch.clientY, e.target);
  };

  useEffect(() => {
    if (!isDragging) return;

    const moveHandler = (clientX: number, clientY: number) => {
        let newX = clientX - dragStartOffset.current.x;
        let newY = clientY - dragStartOffset.current.y;
        
        // Get widget dimensions
        const rect = overlayRef.current?.getBoundingClientRect();
        const width = isMinimized ? 56 : 300;
        const height = rect?.height || 56;
        
        // Viewport constraints - beneath header, avoid fullscreen button (bottom-right 80x80 zone)
        const HEADER_HEIGHT = 80;
        const EDGE_PADDING = 8;
        const BUTTON_ZONE = 80; // Fullscreen button protection zone
        
        newX = Math.max(EDGE_PADDING, Math.min(window.innerWidth - width - EDGE_PADDING, newX));
        newY = Math.max(HEADER_HEIGHT, Math.min(window.innerHeight - height - EDGE_PADDING, newY));
        
        // If in bottom-right corner, push left/up to avoid fullscreen button
        if (newX + width > window.innerWidth - BUTTON_ZONE && newY + height > window.innerHeight - BUTTON_ZONE) {
            newX = Math.min(newX, window.innerWidth - width - BUTTON_ZONE);
        }
        
        onPositionChange({ x: newX, y: newY });
    };

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      moveHandler(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      moveHandler(touch.clientX, touch.clientY);
    };

    const handleUp = () => setIsDragging(false);

    window.addEventListener('mousemove', handleMouseMove, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchend', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
    };
  }, [isDragging, isMinimized, onPositionChange]);

  if (tasks.length === 0) return null;

  if (isMinimized) {
      return (
          <div
            ref={overlayRef}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onDoubleClick={() => setIsMinimized(false)}
            className={`fixed z-[1000] w-14 h-14 rounded-full bg-[rgba(0,0,0,0.98)] border-2 border-white/20 shadow-lg flex items-center justify-center cursor-grab active:cursor-grabbing hover:scale-110 touch-none select-none ${isDragging ? 'cursor-grabbing scale-105' : ''}`}
            style={{ left: position.x, top: position.y, transition: isDragging ? 'none' : 'transform 0.2s' }}
          >
             <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none" viewBox="0 0 36 36">
                <path className="text-white" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                <path className="text-white" strokeDasharray={`${progress}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
             </svg>
             <div className="relative text-white font-black text-xs select-none">{completedCount}/{totalCount}</div>
          </div>
      );
  }

  return (
    <div 
      ref={overlayRef}
      className={`fixed z-[1000] ${isDragging ? 'shadow-2xl opacity-95 cursor-grabbing' : 'shadow-xl'}`}
      style={{ left: position.x, top: position.y, width: '300px', transition: isDragging ? 'none' : 'box-shadow 0.3s' }}
    >
      <div className="bg-[rgba(0,0,0,0.98)] border border-white/20 rounded-2xl overflow-hidden backdrop-blur-2xl">
        <div onMouseDown={handleMouseDown} onTouchStart={handleTouchStart} onDoubleClick={() => setIsMinimized(true)} className="h-12 bg-[rgba(0,0,0,0.98)] border-b border-white/20 flex items-center justify-between px-4 cursor-grab active:cursor-grabbing select-none touch-none">
          <div className="flex items-center gap-2 pointer-events-none">
            <GripVertical size={14} className="text-white" />
            <ListTodo size={16} className="text-white" />
            <span className="text-[10px] font-black uppercase tracking-widest text-white">To-Do List</span>
          </div>
          <div className="flex items-center gap-1">
            {/* Autonomous/Manual Toggle */}
            {onToggleAutonomous && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleAutonomous(); }}
                className={`p-1 rounded transition-colors flex items-center gap-1 ${isAutonomous ? 'text-white bg-[rgba(0,0,0,0.98)]' : 'text-white hover:bg-[rgba(0,0,0,0.98)]'}`}
                title={isAutonomous ? 'Autonomous Mode (QA reviews automatically)' : 'Manual Mode (User reviews)'}
                aria-label={isAutonomous ? 'Switch to Manual' : 'Switch to Autonomous'}
              >
                {isAutonomous ? <Bot size={14} /> : <User size={14} />}
                <span className="text-[8px] font-bold uppercase">{isAutonomous ? 'Auto' : 'Manual'}</span>
              </button>
            )}
            {onTogglePause && (
              <button
                onClick={(e) => { e.stopPropagation(); onTogglePause(); }}
                className={`p-1 rounded transition-colors ${isPaused ? 'text-yellow-400 hover:bg-yellow-500/20' : 'text-white hover:bg-[rgba(0,0,0,0.98)]'}`}
                title={isPaused ? 'Resume agents' : 'Pause agents'}
                aria-label={isPaused ? 'Resume agents' : 'Pause agents'}
              >
                {isPaused ? <Play size={16} /> : <Pause size={16} />}
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); setIsMinimized(true); }} className="p-1 hover:bg-[rgba(0,0,0,0.98)] rounded transition-colors text-white" aria-label="Minimize"><ChevronUp size={16} /></button>
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto p-3 space-y-2 scrollable">
            {tasks.map(task => (
              <div key={task.id} onClick={() => !isDragging && onToggleTask(task.id)} className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer select-none ${task.completed ? 'bg-[var(--glass-strong)] border-transparent opacity-50' : 'bg-[var(--glass-strong)] border-[var(--line-strong)]/40 hover:border-[var(--fg)]/40'}`}>
                <div className="flex items-center gap-2">
                  {!task.completed && onAssignTask && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onAssignTask(task.id, 'Codey'); }}
                      className="p-1.5 rounded-lg bg-[rgba(0,0,0,0.98)] text-white hover:bg-[rgba(0,0,0,0.98)] transition-colors"
                      title="Assign to Codey"
                    >
                      <Bot size={14} />
                    </button>
                  )}
                  <div className="mt-0.5 pointer-events-none">{task.completed ? <CheckCircle2 size={18} className="text-white" /> : <Circle size={18} className="text-white" />}</div>
                </div>
                <div className="flex-1 min-w-0 pointer-events-none">
                  <p className={`text-xs font-medium leading-relaxed truncate ${task.completed ? 'line-through text-white' : 'text-white'}`}>{task.text}</p>
                  {task.assignee && (
                    <div className="flex items-center gap-1 mt-1">
                      <Bot size={10} className="text-white" />
                      <span className="text-[8px] font-bold uppercase text-white">Assigned to {task.assignee}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {allCompleted && (
            <div className="p-3 border-t border-white/20 bg-[rgba(0,0,0,0.98)]">
                <button 
                    onClick={(e) => { e.stopPropagation(); onClear(); }}
                    className="w-full py-2 bg-[rgba(0,0,0,0.98)] text-white rounded-lg text-xs font-black uppercase tracking-wider hover:scale-[1.02] transition-transform shadow-lg"
                >
                    Close To-Do List
                </button>
            </div>
          )}
      </div>
    </div>
  );
};

export default TodoOverlay;
